// ============================================================
//  Topbar — editor controls rendered inside the app header slot
// ============================================================

import React from 'react';
import { useEditor } from '@craftjs/core';
import { useNavigate, useParams } from 'react-router-dom';
import type { PageId } from '../../../types/index';
import routes from '../../../router/routes';

interface Props {
  activePage: PageId;
  device: 'desktop' | 'tablet' | 'mobile';
  unsaved: boolean;
  onSelectHeader: () => void;
  onSetDevice: (d: 'desktop' | 'tablet' | 'mobile') => void;
  onSave: () => void;
  onLoad: () => void;
  onPreview: () => void;
  onPublish: () => void;
  onUnpublish: () => void;
  isPublished: boolean;
  publicUrl?: string | null;
}

const DEVICE_ICONS: Record<'desktop' | 'tablet' | 'mobile', string> = {
  desktop: '🖥',
  tablet: '⬜',
  mobile: '📱',
};

const DEVICE_LABELS: Record<'desktop' | 'tablet' | 'mobile', string> = {
  desktop: 'Desktop',
  tablet: 'Tablet',
  mobile: 'Mobile',
};

const ghostBtn: React.CSSProperties = {
  background: 'none',
  border: 'none',
  color: 'rgba(17,24,39,0.65)',
  borderRadius: 6,
  padding: '5px 10px',
  fontSize: 12,
  fontWeight: 600,
  cursor: 'pointer',
  fontFamily: 'inherit',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  transition: 'all 0.15s',
};

const outlineBtn: React.CSSProperties = {
  ...ghostBtn,
  border: '1px solid rgba(17,24,39,0.2)',
  color: 'rgba(17,24,39,0.85)',
};

const primaryBtn: React.CSSProperties = {
  ...ghostBtn,
  background: '#2563eb',
  color: 'white',
  border: 'none',
};

const dangerBtn: React.CSSProperties = {
  ...ghostBtn,
  color: '#dc2626',
  border: '1px solid rgba(220,38,38,0.3)',
};

const sep: React.CSSProperties = {
  width: 1,
  height: 20,
  background: 'rgba(17,24,39,0.12)',
  flexShrink: 0,
};

export function Topbar({
  activePage,
  device,
  unsaved,
  onSelectHeader,
  onSetDevice,
  onSave,
  onPreview,
  onPublish,
  onUnpublish,
  isPublished,
  publicUrl,
}: Props): React.ReactElement {
  const { actions, canUndo } = useEditor((_, query) => ({
    canUndo: query.history.canUndo(),
  }));
  const navigate = useNavigate();
  const { eventId } = useParams<{ eventId: string }>();

  const handleBack = (): void => {
    if (eventId) {
      navigate(routes.home2.path + `events/${eventId}`);
    } else {
      navigate(-1);
    }
  };

  return (
    <>
      <button
        style={ghostBtn}
        onClick={handleBack}
        title="Retour à l'événement"
      >
        ← Retour
      </button>

      <div style={sep} />

      <button style={ghostBtn} onClick={onSelectHeader}>
        ✏️ Header
      </button>

      <div style={sep} />

      <div style={{ display: 'flex', gap: 2 }}>
        {(['desktop', 'tablet', 'mobile'] as const).map((d) => (
          <button
            key={d}
            onClick={() => onSetDevice(d)}
            title={DEVICE_LABELS[d]}
            style={{
              ...ghostBtn,
              color: device === d ? '#111827' : 'rgba(17,24,39,0.4)',
              background: device === d ? 'rgba(17,24,39,0.08)' : 'none',
              border:
                device === d
                  ? '1px solid rgba(17,24,39,0.2)'
                  : '1px solid transparent',
            }}
          >
            <span>{DEVICE_ICONS[d]}</span>
            <span>{DEVICE_LABELS[d]}</span>
          </button>
        ))}
      </div>

      <div style={sep} />

      <button
        style={{ ...ghostBtn, opacity: canUndo ? 1 : 0.35 }}
        onClick={() => actions.history.undo()}
        disabled={!canUndo}
        title="Annuler (Ctrl+Z)"
      >
        ↩ Annuler
      </button>

      {unsaved && (
        <span
          style={{
            width: 7,
            height: 7,
            borderRadius: '50%',
            background: '#f59e0b',
            flexShrink: 0,
          }}
          title="Modifications non sauvegardées"
        />
      )}

      <button style={outlineBtn} onClick={onSave}>
        💾 Sauvegarder
      </button>

      <button style={outlineBtn} onClick={onPreview}>
        👁 Aperçu
      </button>

      {isPublished && publicUrl && (
        <a
          href={publicUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            ...outlineBtn,
            textDecoration: 'none',
            color: '#16a34a',
            borderColor: 'rgba(22,163,74,0.35)',
            maxWidth: 200,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
          title={publicUrl}
        >
          🔗 {publicUrl.replace(/^https?:\/\//, '')}
        </a>
      )}

      {isPublished ? (
        <button
          style={dangerBtn}
          onClick={onUnpublish}
          title="Retirer la page publiée"
        >
          ⏸ Dépublier
        </button>
      ) : (
        <button style={primaryBtn} onClick={onPublish} title="Publier en ligne">
          🚀 Publier
        </button>
      )}
    </>
  );
}
